// src/components/navigation/LandingNavigation.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { signOutUser } from '../../firebase/auth';
import ThemeToggle from '../common/ThemeToggle';

// Eight ball fallback avatar
const EIGHT_BALL_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8IS0tIE91dGVyIGJsYWNrIGNpcmNsZSAtLT4KICA8Y2lyY2xlIGN4PSIxNiIgY3k9IjE2IiByPSIxNSIgZmlsbD0iIzAwMCIgc3Ryb2tlPSIjMzMzIiBzdHJva2Utd2lkdGg9IjIiLz4KICA8IS0tIFdoaXRlIGNpcmNsZSBmb3IgbnVtYmVyIC0tPgogIDxjaXJjbGUgY3g9IjE2IiBjeT0iMTYiIHI9IjciIGZpbGw9IndoaXRlIi8+CiAgPCEtLSBOdW1iZXIgOCAtLT4KICA8dGV4dCB4PSIxNiIgeT0iMjAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IiMwMDAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPjg8L3RleHQ+Cjwvc3ZnPgo=';

// Sync indicator dot — surfaces real-time Firestore sync status to the director so
// they can tell when their edits are still in flight or when the network has dropped.
function SyncIndicator({ syncStatus, lastSyncedAt }) {
  if (!syncStatus) return null;

  const palette = {
    synced:  { color: '#22c55e', label: 'Synced' },
    syncing: { color: '#f59e0b', label: 'Saving…' },
    offline: { color: '#9ca3af', label: 'Offline — changes saved locally' },
    error:   { color: '#ef4444', label: 'Sync failed — will retry' }
  };
  const { color, label } = palette[syncStatus] || palette.synced;

  let tooltip = label;
  if (syncStatus === 'synced' && lastSyncedAt) {
    const seconds = Math.max(0, Math.round((Date.now() - lastSyncedAt) / 1000));
    if (seconds < 5) tooltip = 'Synced just now';
    else if (seconds < 60) tooltip = `Synced ${seconds}s ago`;
    else tooltip = `Synced ${Math.round(seconds / 60)}m ago`;
  }

  return (
    <span
      title={tooltip}
      aria-label={tooltip}
      style={{
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: color,
        boxShadow: `0 0 0 2px var(--bg-secondary), 0 0 6px ${color}66`,
        animation: syncStatus === 'syncing' ? 'kcpoolSyncPulse 1.2s ease-in-out infinite' : 'none',
        flexShrink: 0
      }}
    />
  );
}

// Shared button styles
const navButtonStyles = {
  base: {
    padding: '10px 20px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
    fontFamily: 'var(--font-display)',
    position: 'relative',
    overflow: 'hidden',
  },
  primary: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 8px rgba(102, 126, 234, 0.25)',
  },
  secondary: {
    background: 'transparent',
    color: '#667eea',
    border: '2px solid #667eea',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-color)',
  }
};

export default function LandingNavigation({
  currentView,
  onNavigate,
  onShowAuth,
  onSaveToCloud,
  onShowShare,
  onShowProfile,
  onCreateTournament,
  syncStatus,
  lastSyncedAt
}) {
  const { isAuthenticated, displayName, photoURL } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Use hamburger menu for screens under 1000px wide OR landscape mobile (short height)
  const checkIfMobile = () => {
    const isNarrow = window.innerWidth < 1000;
    const isLandscapeMobile = window.innerHeight < 500 && window.innerWidth < 1200;
    return isNarrow || isLandscapeMobile;
  };

  const [isMobile, setIsMobile] = useState(checkIfMobile());

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const shouldBeMobile = checkIfMobile();
      setIsMobile(shouldBeMobile);
      if (!shouldBeMobile) {
        setShowMobileMenu(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle scroll for glass effect enhancement
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle Create Tournament button click
  const handleCreateClick = () => {
    if (isAuthenticated && onCreateTournament) {
      // If authenticated and callback provided, open create modal
      onCreateTournament();
    } else {
      // Otherwise navigate to local tournament mode
      onNavigate('local');
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    const { error } = await signOutUser();
    if (error) {
      console.error('Sign out error:', error);
      alert('Failed to sign out. Please try again.');
    } else {
      // Navigate to landing page after sign out
      onNavigate('landing');
    }
  };

  // Hide "Create Tournament" when actively viewing a tournament
  const isViewingTournament = currentView === 'local' || currentView === 'firebase' || currentView === 'tournament';

  const navItems = [
    { id: 'finder', label: 'Find Tournaments', visible: true, onClick: () => onNavigate('finder') },
    { id: 'leaderboard', label: 'Rankings', visible: true, onClick: () => onNavigate('leaderboard') },
    { id: 'local', label: 'Create Tournament', visible: !isViewingTournament, onClick: handleCreateClick },
    { id: 'dashboard', label: 'Dashboard', visible: isAuthenticated, onClick: () => onNavigate('dashboard') },
    { id: 'help', label: 'Help', visible: true, onClick: () => onNavigate('help') }
  ];

  return (
    <nav style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: isScrolled ? 'var(--bg-glass)' : 'var(--bg-secondary)',
      backdropFilter: isScrolled ? 'blur(12px)' : 'none',
      WebkitBackdropFilter: isScrolled ? 'blur(12px)' : 'none',
      borderBottom: `1px solid ${isScrolled ? 'rgba(255,255,255,0.1)' : 'var(--border-light)'}`,
      padding: '0 20px',
      zIndex: 1000,
      boxShadow: isScrolled ? 'var(--nav-shadow-scroll)' : 'var(--nav-shadow)',
      transition: 'all 0.25s ease'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '64px'
      }}>
        {/* Logo */}
        <div
          onClick={() => onNavigate('landing')}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
          }}
        >
          <img
            src="/Logo_horizontal.png"
            alt="Pool Tournaments"
            style={{
              height: isMobile ? '32px' : '40px',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Mobile Menu Toggle */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <SyncIndicator syncStatus={syncStatus} lastSyncedAt={lastSyncedAt} />
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              style={{
                padding: '8px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'var(--text-primary)'
              }}
            >
              {showMobileMenu ? '✕' : '☰'}
            </button>
          </div>
        )}

        {/* Desktop Navigation */}
        {!isMobile && (
          <>
            {/* Nav Items */}
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              {navItems
                .filter(item => item.visible)
                .map(item => (
                  <button
                    key={item.id}
                    onClick={item.onClick}
                    style={{
                      ...navButtonStyles.base,
                      ...(currentView === item.id ? navButtonStyles.primary : navButtonStyles.secondary),
                    }}
                    onMouseEnter={(e) => {
                      if (currentView !== item.id) {
                        e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                        e.target.style.color = '#fff';
                        e.target.style.borderColor = 'transparent';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.35)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentView !== item.id) {
                        e.target.style.background = 'transparent';
                        e.target.style.color = '#667eea';
                        e.target.style.borderColor = '#667eea';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {item.label}
                  </button>
                ))}
            </div>

            {/* Auth Section */}
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center'
            }}>
              {/* Sync status indicator (only when caller provides syncStatus) */}
              <SyncIndicator syncStatus={syncStatus} lastSyncedAt={lastSyncedAt} />

              {/* Theme Toggle */}
              <ThemeToggle variant="switch" />

              {/* Save to Cloud button only when on local tournament view */}
              {currentView === 'local' && onSaveToCloud && (
                <button
                  onClick={onSaveToCloud}
                  style={{
                    ...navButtonStyles.base,
                    ...navButtonStyles.primary,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 15px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 8px rgba(102, 126, 234, 0.25)';
                  }}
                >
                  ☁️ Save to Cloud
                </button>
              )}

              {/* Share button only when viewing a tournament */}
              {onShowShare && (
                <button
                  onClick={onShowShare}
                  style={{
                    ...navButtonStyles.base,
                    ...navButtonStyles.primary,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 15px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 8px rgba(102, 126, 234, 0.25)';
                  }}
                >
                  🔗 Share
                </button>
              )}

              {/* User Profile or Sign In */}
              {!isAuthenticated ? (
                <>
                  <button
                    onClick={() => onShowAuth('login')}
                    style={{
                      ...navButtonStyles.base,
                      ...navButtonStyles.secondary,
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                      e.target.style.color = '#fff';
                      e.target.style.borderColor = 'transparent';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.color = '#667eea';
                      e.target.style.borderColor = '#667eea';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Login
                  </button>
                  <button
                    onClick={() => onShowAuth('signup')}
                    style={{
                      ...navButtonStyles.base,
                      ...navButtonStyles.primary,
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 15px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 8px rgba(102, 126, 234, 0.25)';
                    }}
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <>
                  {/* My Stats Button */}
                  <button
                    onClick={() => onNavigate('profile')}
                    style={{
                      ...navButtonStyles.base,
                      ...navButtonStyles.secondary,
                      padding: '8px 16px',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                      e.target.style.color = '#fff';
                      e.target.style.borderColor = 'transparent';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.color = '#667eea';
                      e.target.style.borderColor = '#667eea';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    My Stats
                  </button>
                  <div
                    onClick={onShowProfile ? onShowProfile : () => onNavigate('dashboard')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      backgroundColor: 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <img
                      src={photoURL || EIGHT_BALL_AVATAR}
                      alt={displayName || 'User'}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.src = EIGHT_BALL_AVATAR;
                      }}
                    />
                    <span style={{
                      fontWeight: 'bold',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}>
                      {displayName || 'User'}
                    </span>
                  </div>
                  <button
                    onClick={handleSignOut}
                    style={{
                      ...navButtonStyles.base,
                      ...navButtonStyles.ghost,
                      padding: '8px 16px',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-hover)';
                      e.target.style.borderColor = 'var(--text-muted)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = 'var(--border-color)';
                    }}
                  >
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobile && showMobileMenu && (
        <div style={{
          position: 'absolute',
          top: '64px',
          left: 0,
          right: 0,
          backgroundColor: 'var(--bg-secondary)',
          borderBottom: '2px solid var(--border-color)',
          boxShadow: 'var(--shadow-lg)',
          padding: '16px',
          maxHeight: 'calc(100vh - 64px)',
          overflowY: 'auto'
        }}>
          {/* Nav Items */}
          {navItems
            .filter(item => item.visible)
            .map(item => (
              <button
                key={item.id}
                onClick={() => {
                  item.onClick();
                  setShowMobileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '8px',
                  backgroundColor: currentView === item.id ? '#667eea' : 'transparent',
                  color: currentView === item.id ? '#fff' : '#667eea',
                  border: currentView === item.id ? 'none' : '2px solid #667eea',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                {item.label}
              </button>
            ))}

          {/* Action Buttons */}
          {currentView === 'local' && onSaveToCloud && (
            <button
              onClick={() => {
                onSaveToCloud();
                setShowMobileMenu(false);
              }}
              style={{
                width: '100%',
                padding: '14px 16px',
                marginBottom: '8px',
                backgroundColor: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              ☁️ Save to Cloud
            </button>
          )}

          {/* Share button for tournament view */}
          {onShowShare && (
            <button
              onClick={() => {
                onShowShare();
                setShowMobileMenu(false);
              }}
              style={{
                width: '100%',
                padding: '14px 16px',
                marginBottom: '8px',
                backgroundColor: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              🔗 Share
            </button>
          )}

          {/* Theme Toggle - Mobile */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            marginBottom: '8px',
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: '6px'
          }}>
            <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>Theme</span>
            <ThemeToggle variant="switch" />
          </div>

          {/* User Profile or Auth */}
          {!isAuthenticated ? (
            <>
              <button
                onClick={() => {
                  onShowAuth('login');
                  setShowMobileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '8px',
                  backgroundColor: 'transparent',
                  color: '#667eea',
                  border: '2px solid #667eea',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Login
              </button>
              <button
                onClick={() => {
                  onShowAuth('signup');
                  setShowMobileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  backgroundColor: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Sign Up
              </button>
            </>
          ) : (
            <>
              {/* My Stats Button - Mobile */}
              <button
                onClick={() => {
                  onNavigate('profile');
                  setShowMobileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '8px',
                  backgroundColor: 'transparent',
                  color: '#667eea',
                  border: '2px solid #667eea',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                My Stats
              </button>
              <button
                onClick={() => {
                  if (onShowProfile) {
                    onShowProfile();
                  } else {
                    onNavigate('dashboard');
                  }
                  setShowMobileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: '8px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <img
                  src={photoURL || EIGHT_BALL_AVATAR}
                  alt={displayName || 'User'}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.src = EIGHT_BALL_AVATAR;
                  }}
                />
                <span>{displayName || 'Profile'}</span>
              </button>
              <button
                onClick={() => {
                  handleSignOut();
                  setShowMobileMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Sign Out
              </button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
